import React, { useState, useContext, useEffect, useMemo } from 'react';

import { PageHeader, Row, Col } from 'antd';

import styled from 'styled-components';

import { saveAlerts } from '@libs/NwAlerts';

import { useStateValue } from '@contexts/StateProvider';
import { UserContext, useUserPermission } from '@contexts/UserContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { modelFullName } from '@hooks/API/Contact/UseContactsList';
import { useAddContactTag, useRemoveContactTag } from '@hooks/API/Contact/UseContactTags';
import useConfigurations from '@hooks/Configurations/UseConfigurations';
import useLocalStorageState from '@hooks/Utils/UseLocalStorageState';
import usePlaceholderImageFixed from '@hooks/Utils/UsePlaceholderImageFixed';

import AnagraphicalDrawer from '@components/ContactDetails/CommonComponents/Anagraphical/AnagraphicalDrawer';
import ModelDetailsCover from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsCover';
import ModelDetailsHeaderAgencies from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsHeader/ModelDetailsHeaderAgencies';

import AlertDrawer from '@components/AlertDrawer/AlertDrawer';
import BillingInfoItemContactSummary from '@components/BillingInfos/BillingInfoItemContactSummary';
import ContactAlert from '@components/ContactAlert/ContactAlert';
import ContactAlertDrawer from '@components/ContactAlert/ContactAlertDrawer';
import ContactDepartments from '@components/ContactDepartments/ContactDepartments';
import ContactApproval from '@components/ContactDetails/ContactDetailsHeader/ContactApproval';
import ContactDetailsBankAccounts from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsBankAccounts';
import ContactDetailsHeaderAnagraphical from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderAnagraphical';
import ContactDetailsHeaderConnections from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderConnections';
import ContactDetailsHeaderLegal from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsHeaderLegal';
import ContactDetailsInAgencySince from '@components/ContactDetails/ContactDetailsHeader/ContactDetailsInAgencySince';
import ContactHeaderBillingInfos from '@components/ContactDetails/ContactDetailsHeader/ContactHeaderBillingInfos';
import ContactDetailsNotes from '@components/ContactDetailsNotes/ContactDetailsNotes';
import NoteAddEditDrawer from '@components/ContactDetailsNotes/NoteAddEditDrawer';
import ModelAgencyCommissionDrawer from '@components/ContactsCommissionsDrawers/ModelAgencyCommissionDrawer';
import AddToFolderButton from '@components/ContactsFolders/AddToFolderButton';
import ContactUsers from '@components/ContactUsers/ContactUsers';
import CreatedBy from '@components/CreatedBy/CreatedBy';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwAddButton, NwLinkButton } from '@components/Gui/NwButton';
import NwDividerVertical from '@components/Gui/NwDividerVertical';
import NwDrawer from '@components/Gui/NwDrawer';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import ModelInTown from '@components/ModelInTown/ModelInTown';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';
import ObjectTagPicker from '@components/TagPicker/ObjectTagPicker';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ModelPageHeader = styled(PageHeader)`
    --ant-page-header-height: 17rem;
    --ant-page-header-width: 13rem;

    height: var(--ant-page-header-height) !important;

    .ant-page-header-heading {
        padding-left: calc(var(--ant-page-header-width) + 1.5rem) !important;
    }

    .ant-page-header-content {
        .header-image-container {
            height: var(--ant-page-header-height) !important;
            width: var(--ant-page-header-width) !important;
        }
    }

    .header-content {
        padding-left: calc(var(--ant-page-header-width) + 1.5rem) !important;
        .header-body {
            padding-right: 3rem !important;

            .header-models-notes-container {
                border-left: ${(props) => props.theme.LighterBorder};
                padding-left: 1rem;
                padding-top: 0.15rem;

                .header-models-notes-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;

                    h2 {
                        font-size: ${(props) => props.theme.FontSizeSm};
                        text-transform: uppercase;
                        font-weight: 500;
                    }
                }

                .header-models-notes {
                    .header-models-note {
                        margin-bottom: 0.25rem;
                        font-size: ${(props) => props.theme.FontSizeSm};
                    }
                }
                .header-models-notes-more {
                    button {
                        span {
                            font-size: ${(props) => props.theme.FontSizeSm};
                        }
                    }
                }
            }
        }
    }
    .ant-page-header-heading-title {
        width: 100%;
    }
    .ant-page-header-heading-left {
        width: 100%;
    }

    .info-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 0.5rem;
        line-height: 1.2em;
        margin-bottom: 0.15rem;
        font-size: ${(props) => props.theme.FontSizeSm};

        span {
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
            min-width: 8rem;
        }
        strong {
            font-weight: 500;
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColor};
        }
    }
`;

const ModelDetailsHeaderDrawerAccounting = ({ currentContact, billingInfos }) => {
    const [{ areaContext }] = useStateValue();
    const { mutate: addTag } = useAddContactTag();
    const { mutate: removeTag } = useRemoveContactTag();
    const configurations = useConfigurations();
    const { currentUser } = useContext(UserContext);
    const [IsLoading, setIsLoading] = useState(true);
    const [showAlertDrawer, setShowAlertDrawer] = useState(false);
    const [readAlerts, setReadAlerts] = useLocalStorageState(`${areaContext.area}-read-alerts`, []);
    //const [isOpenHistory, setIsOpenHistory] = useState(false);
    const alertOnModels = useUserPermission(
        'AlertOnModels',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const tagsOnContacts = useUserPermission(
        'TagsOnContacts',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const [anagraphicalDrawer, setAnagraphicalDrawer] = useState(false);

    const [showCommissionDrawer, setShowCommissionDrawer] = useState(false);
    const [showNotesDrawer, setShowNotesDrawer] = useState(false);
    const [showNewNoteDrawer, setShowNewNoteDrawer] = useState(false);

    const getContactOrDefaultModelCommission = () => {
        const modelCommission = currentContact.NetworkAgencies[0].ModelCommission;
        const isDefaultModelCommission = modelCommission === null || modelCommission === undefined;

        return isDefaultModelCommission
            ? `${configurations.defaultAgencyCommissionPerc}% [default]`
            : `${modelCommission}%`;
    };

    const handleOpenAnagraphicalDrawer = () => {
        setAnagraphicalDrawer(true);
    };

    useEffect(() => {
        if (currentContact) {
            setIsLoading(false);
        }
    }, [currentContact]);

    // const handleOpenHistory = () => {
    //     setIsOpenHistory(true);
    // };

    // const handleCloseHistory = () => {
    //     setIsOpenHistory(false);
    // };

    const onAddTag = (tag) => {
        addTag({ tag: tag, contactID: currentContact.ID });
    };

    const onRemoveTag = (tag) => {
        removeTag({ tag: tag, contactID: currentContact.ID });
    };

    const afterNotesCreation = () => {
        forceReloadContact(currentContact.ID);
        setShowNotesDrawer(false);
    };

    const afterNewNoteCreation = () => {
        forceReloadContact(currentContact.ID);
        setShowNewNoteDrawer(false);
    };

    const handleOpenAlertDrawer = () => {
        setShowAlertDrawer(true);
    };

    const handleCloseAlertDrawer = (updated) => {
        if (updated) {
            const alerts = readAlerts.filter(
                (alert) => alert.Author !== currentUser.UserID || alert.ContactID !== currentContact.ID
            );
            setReadAlerts(alerts);
            saveAlerts(alerts);
        }
        setShowAlertDrawer(false);
    };

    const coverguid =
        currentContact && currentContact.NetworkAgencies[0] && currentContact.NetworkAgencies[0].CoverGuid;
    const subType = currentContact && currentContact.SubType;

    const [urlbg, isPlaceholder] = usePlaceholderImageFixed(coverguid, subType);

    const billingNotes = useMemo(() => {
        return currentContact.Notes.filter((x) => x.NoteTypeCode === 'BILLING');
    }, [currentContact]);

    return currentContact ? (
        IsLoading ? (
            <Loading />
        ) : (
            <>
                {!currentContact.Approved && <ContactApproval contact={currentContact} />}
                <ContactAlertDrawer contactId={currentContact.ID} />
                <div className="details-header-container details-header-container-drawer">
                    <ModelPageHeader
                        onBack={null}
                        title={<FlexContainer wrap="wrap" justifyContent="space-between" fullWidth></FlexContainer>}
                        subTitle={false}
                    >
                        <ModelDetailsCover
                            urlBg={urlbg}
                            isPlaceholder={isPlaceholder}
                            currentContact={currentContact}
                            readOnly
                        />
                        <div className="header-content">
                            <div className="header-body">
                                <Row gutter={15}>
                                    <Col span={14}>
                                        <div className="header-body-name-container">
                                            <div className="name-wrapper">
                                                <h1 onClick={handleOpenAnagraphicalDrawer}>
                                                    {modelFullName(currentContact).toLowerCase()}
                                                </h1>
                                                {anagraphicalDrawer && (
                                                    <AnagraphicalDrawer
                                                        currentContact={currentContact}
                                                        type={1}
                                                        onClose={() => {
                                                            setAnagraphicalDrawer(false);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <span className="name-icon">
                                                <AddToFolderButton
                                                    ids={[currentContact.ID]}
                                                    type="model"
                                                    size="lg"
                                                    showBadge
                                                />
                                            </span>
                                            <span className="name-icon">
                                                {alertOnModels ? (
                                                    <NwIcon
                                                        icon={byPrefixAndName.fasl['comment-alt-exclamation']}
                                                        plus={!currentContact.Alert}
                                                        edit={currentContact.Alert}
                                                        pointer
                                                        onClick={handleOpenAlertDrawer}
                                                    />
                                                ) : (
                                                    <ContactAlert
                                                        currentContact={currentContact}
                                                        readOnly
                                                        icon={
                                                            <NwIcon
                                                                size="lg"
                                                                icon={byPrefixAndName.fasl['comment-alt-exclamation']}
                                                                pointer
                                                            />
                                                        }
                                                    />
                                                )}
                                            </span>
                                            <span className="name-icon">
                                                <PermissionBlock
                                                    permission={tagsOnContacts}
                                                    content={
                                                        <ObjectTagPicker
                                                            readOnly={!tagsOnContacts}
                                                            itemType={currentContact.ContactType}
                                                            tags={currentContact.Tags}
                                                            onAddTag={tagsOnContacts && onAddTag}
                                                            onRemoveTag={tagsOnContacts && onRemoveTag}
                                                            stripMaxWidth="14rem"
                                                        />
                                                    }
                                                />
                                            </span>
                                        </div>
                                        <Row gutter={15}>
                                            <Col span={12}>
                                                <div className="header-body-boxes-container">
                                                    <ModelInTown
                                                        modelID={currentContact.ID}
                                                        modelInTownSchedule={currentContact.InTownSchedule}
                                                    />
                                                    <NwDividerVertical />
                                                    <ModelDetailsHeaderAgencies model={currentContact} />
                                                </div>
                                                <div className="header-body-boxes-container">
                                                    <ContactDetailsInAgencySince contact={currentContact} />
                                                </div>
                                                <div className="header-body-boxes-container">
                                                    <div className="header-body-boxes header-body-boxes-hoverable">
                                                        <ContactUsers
                                                            users={currentContact.Users}
                                                            contactID={currentContact.ID}
                                                        />
                                                    </div>
                                                    <NwDividerVertical />
                                                    <div className="header-body-boxes">
                                                        <ContactDepartments
                                                            departments={currentContact.Departments}
                                                            contactID={currentContact.ID}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="header-body-boxes-container model-info-line">
                                                    <ContactDetailsHeaderAnagraphical contact={currentContact} />
                                                    <div className="header-models-counters">
                                                        <NwDividerVertical />
                                                        <ContactDetailsHeaderLegal currentContact={currentContact} />
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col span={12}>
                                                {currentContact.NetworkAgencies &&
                                                    currentContact.NetworkAgencies.length === 1 && (
                                                        <div className="info-row">
                                                            <span>Agency Commission</span>
                                                            <strong>{getContactOrDefaultModelCommission()}</strong>
                                                            <NwIcon
                                                                icon={byPrefixAndName.fasl['edit']}
                                                                primary
                                                                pointer
                                                                style={{ marginLeft: '.5rem' }}
                                                                onClick={() => setShowCommissionDrawer(true)}
                                                            />
                                                        </div>
                                                    )}
                                                {billingInfos && billingInfos.length > 0 && (
                                                    <BillingInfoItemContactSummary
                                                        item={billingInfos[0]}
                                                        currentContact={currentContact}
                                                    />
                                                )}
                                                <div
                                                    className="header-body-boxes-container"
                                                    style={{ marginTop: '.5rem' }}
                                                >
                                                    <ContactHeaderBillingInfos currentContact={currentContact} />
                                                </div>
                                                <div
                                                    className="header-body-boxes-container"
                                                    style={{ marginTop: '.5rem' }}
                                                >
                                                    <ContactDetailsBankAccounts currentContact={currentContact} />
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col span={10}>
                                        <div className="header-models-notes-container">
                                            <div className="header-models-notes-header">
                                                <h2>Billing Notes</h2>
                                                <NwIcon
                                                    pointer
                                                    icon={byPrefixAndName.fasl['sidebar-flip']}
                                                    onClick={() => setShowNotesDrawer(true)}
                                                />
                                            </div>
                                            {currentContact.Notes && billingNotes.length > 0 && (
                                                <>
                                                    <div className="header-models-notes">
                                                        {billingNotes.slice(0, 2).map((x) => (
                                                            <div className="header-models-note" key={x.ID}>
                                                                <NwEllipsis text={x.Text} popover rows={2} />
                                                                <CreatedBy object={x} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {billingNotes.length > 2 && (
                                                        <div className="header-models-notes-more">
                                                            <NwLinkButton
                                                                primary
                                                                label={`${billingNotes.length - 2} note${billingNotes.length - 2 === 1 ? '' : 's'} more`}
                                                                onClick={() => setShowNotesDrawer(true)}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                        <div className="header-footer">
                            <ContactDetailsHeaderConnections contact={currentContact} inFooter />
                        </div>
                    </ModelPageHeader>
                </div>
                {showAlertDrawer && <AlertDrawer currentContact={currentContact} onClose={handleCloseAlertDrawer} />}
                {showCommissionDrawer && (
                    <ModelAgencyCommissionDrawer
                        networkAgency={currentContact.NetworkAgencies[0]}
                        model={currentContact}
                        //onSetModel={handleModelReload}
                        onClose={() => setShowCommissionDrawer(false)}
                    />
                )}
                {showNotesDrawer && (
                    <NwDrawer
                        title="Billing Notes"
                        onClose={() => setShowNotesDrawer(false)}
                        extra={<NwAddButton ghost onClick={() => setShowNewNoteDrawer(true)} />}
                    >
                        <ContactDetailsNotes
                            currentContact={currentContact}
                            isInCardTab
                            drawerMode
                            afterNotesCreation={afterNotesCreation}
                            noEllipsis
                            noPagination
                            type="billing"
                        />
                    </NwDrawer>
                )}
                {showNewNoteDrawer && (
                    <NwDrawer title="New Billing Note" onClose={() => setShowNewNoteDrawer(false)}>
                        <NoteAddEditDrawer
                            contactID={currentContact.ID}
                            billingNote
                            afterNotesCreation={afterNewNoteCreation}
                        />
                    </NwDrawer>
                )}
            </>
        )
    ) : (
        <Loading />
    );
};

export default ModelDetailsHeaderDrawerAccounting;
